import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import {
    X,
} from "lucide-react";

import {
    useNavigate,
} from "react-router-dom";

import RouteConfig from "../../routes/RouteConfig";


// ── 플레이잉 카드 데이터 ──────────────────────────────────────────
const CARDS = [
    {
        rank: "A",
        suit: "♠",
        top: "8%",
        left: "4%",
        delay: "0s",
        dur: "5.5s",
        rot: "-18deg",
    },
    {
        rank: "K",
        suit: "♥",
        top: "12%",
        left: "87%",
        delay: "0.6s",
        dur: "6.2s",
        rot: "14deg",
    },
    {
        rank: "Q",
        suit: "♦",
        top: "62%",
        left: "2%",
        delay: "1.1s",
        dur: "5.8s",
        rot: "9deg",
    },
    {
        rank: "J",
        suit: "♣",
        top: "72%",
        left: "91%",
        delay: "1.7s",
        dur: "6.5s",
        rot: "-12deg",
    },
    {
        rank: "10",
        suit: "♠",
        top: "28%",
        left: "92%",
        delay: "0.3s",
        dur: "7s",
        rot: "22deg",
    },
    {
        rank: "9",
        suit: "♥",
        top: "78%",
        left: "6%",
        delay: "2s",
        dur: "5.3s",
        rot: "-6deg",
    },
    {
        rank: "A",
        suit: "♦",
        top: "42%",
        left: "95%",
        delay: "1.4s",
        dur: "6.8s",
        rot: "-20deg",
    },
    {
        rank: "K",
        suit: "♣",
        top: "18%",
        left: "1%",
        delay: "0.9s",
        dur: "5.6s",
        rot: "7deg",
    },
    {
        rank: "Q",
        suit: "♠",
        top: "55%",
        left: "88%",
        delay: "2.3s",
        dur: "6.1s",
        rot: "-15deg",
    },
    {
        rank: "J",
        suit: "♥",
        top: "85%",
        left: "78%",
        delay: "0.5s",
        dur: "7.2s",
        rot: "18deg",
    },
    {
        rank: "7",
        suit: "♦",
        top: "5%",
        left: "55%",
        delay: "1.8s",
        dur: "5.9s",
        rot: "-9deg",
    },
    {
        rank: "2",
        suit: "♣",
        top: "90%",
        left: "42%",
        delay: "2.6s",
        dur: "6.4s",
        rot: "11deg",
    },
];


// ── 포커 칩 데이터 ────────────────────────────────────────────────
const CHIPS = [
    {
        color: "#D59C39",
        inner: "#a87820",
        top: "45%",
        left: "7%",
        delay: "0.8s",
        dur: "6.8s",
    },
    {
        color: "#dc2626",
        inner: "#991b1b",
        top: "33%",
        left: "91%",
        delay: "2.1s",
        dur: "5.7s",
    },
    {
        color: "#1d4ed8",
        inner: "#1e3a8a",
        top: "68%",
        left: "14%",
        delay: "1.3s",
        dur: "7.1s",
    },
    {
        color: "#16a34a",
        inner: "#14532d",
        top: "22%",
        left: "78%",
        delay: "0.4s",
        dur: "6.3s",
    },
    {
        color: "#7c3aed",
        inner: "#4c1d95",
        top: "80%",
        left: "55%",
        delay: "1.9s",
        dur: "5.5s",
    },
];


const NOTICE_SESSION_KEY =
    "lapoit-landing-marketing-notice-seen";


const isRedSuit = (
    suit: string,
) =>
    suit === "♥" ||
    suit === "♦";


// ── 포커 칩 SVG 컴포넌트 ─────────────────────────────────────────
const PokerChip:
    React.FC<{
        color: string;
        inner: string;
        size?: number;
    }> = ({
        color,
        inner,
        size = 52,
    }) => (
        <svg
            width={size}
            height={size}
            viewBox="0 0 52 52"
            fill="none"
        >
            <circle
                cx="26"
                cy="26"
                r="25"
                fill={color}
                stroke="#fff"
                strokeWidth="2"
            />

            <circle
                cx="26"
                cy="26"
                r="18"
                fill={inner}
                stroke="#fff"
                strokeWidth="1.5"
                strokeDasharray="4 3"
            />

            <circle
                cx="26"
                cy="26"
                r="10"
                fill={color}
            />

            <text
                x="26"
                y="30"
                textAnchor="middle"
                fill="#fff"
                fontSize="10"
                fontWeight="bold"
            >
                ★
            </text>
        </svg>
    );


// ── 스크롤 페이드인 훅 ────────────────────────────────────────────
const useFadeIn = () => {
    const ref =
        useRef<HTMLDivElement>(
            null,
        );

    useEffect(() => {
        const el =
            ref.current;

        if (!el) {
            return;
        }

        const obs =
            new IntersectionObserver(
                ([entry]) => {
                    if (
                        entry.isIntersecting
                    ) {
                        el.classList.add(
                            "visible",
                        );

                        obs.disconnect();
                    }
                },
                {
                    threshold: 0.15,
                },
            );

        obs.observe(el);

        return () =>
            obs.disconnect();
    }, []);

    return ref;
};


// ── 섹션 공통 래퍼 ───────────────────────────────────────────────
const FadeSection:
    React.FC<{
        children:
            React.ReactNode;
        className?: string;
        delay?: string;
    }> = ({
        children,
        className = "",
        delay,
    }) => {
        const ref =
            useFadeIn();

        return (
            <div
                ref={ref}
                className={`landing-fade-in ${className}`}
                style={
                    delay
                        ? {
                              transitionDelay:
                                  delay,
                          }
                        : undefined
                }
            >
                {children}
            </div>
        );
    };


// ── 메인 컴포넌트 ────────────────────────────────────────────────
const LandingPage:
    React.FC = () => {
    const navigate =
        useNavigate();

    const navRef =
        useRef<HTMLElement>(
            null,
        );


    /**
     * =========================================================
     * 마케팅 팝업
     *
     * 한 브라우저 세션에서 최초 1회만 표시
     * =========================================================
     */
    const [
        isNoticeOpen,
        setIsNoticeOpen,
    ] =
        useState<boolean>(
            () => {
                try {
                    return (
                        window
                            .sessionStorage
                            .getItem(
                                NOTICE_SESSION_KEY,
                            ) !==
                        "true"
                    );
                } catch {
                    return true;
                }
            },
        );


    /**
     * =========================================================
     * 팝업 닫기
     * =========================================================
     */
    const handleCloseNotice =
        useCallback(
            (): void => {
                try {
                    window
                        .sessionStorage
                        .setItem(
                            NOTICE_SESSION_KEY,
                            "true",
                        );
                } catch {
                    // storage 사용 불가 환경
                }

                setIsNoticeOpen(
                    false,
                );
            },
            [],
        );


    /**
     * =========================================================
     * 팝업 열려있는 동안
     * body scroll 방지
     * =========================================================
     */
    useEffect(
        () => {
            if (
                !isNoticeOpen
            ) {
                return;
            }

            const previousOverflow =
                document.body
                    .style
                    .overflow;

            document.body
                .style
                .overflow =
                "hidden";

            return () => {
                document.body
                    .style
                    .overflow =
                    previousOverflow;
            };
        },
        [
            isNoticeOpen,
        ],
    );

    /**
     * 네비바 스크롤 효과
     */
    useEffect(() => {
        const handler =
            () => {
                if (
                    !navRef.current
                ) {
                    return;
                }

                if (
                    window.scrollY >
                    40
                ) {
                    navRef.current
                        .style
                        .background =
                        "rgba(14,14,15,0.92)";

                    navRef.current
                        .style
                        .backdropFilter =
                        "blur(12px)";

                    navRef.current
                        .style
                        .boxShadow =
                        "0 2px 24px rgba(0,0,0,0.5)";
                } else {
                    navRef.current
                        .style
                        .background =
                        "transparent";

                    navRef.current
                        .style
                        .backdropFilter =
                        "none";

                    navRef.current
                        .style
                        .boxShadow =
                        "none";
                }
            };

        window.addEventListener(
            "scroll",
            handler,
            {
                passive: true,
            },
        );

        return () =>
            window.removeEventListener(
                "scroll",
                handler,
            );
    }, []);


    const feltTexture =
        `radial-gradient(
            ellipse at 40% 30%,
            #0f2d1a 0%,
            #091a0e 45%,
            #0E0E0F 100%
        )`;


    return (
        <>

            {/* =================================================
                이벤트 / 공지 이미지 팝업
                ================================================= */}
            {isNoticeOpen && (

                <div
                    className="
                        fixed
                        inset-0
                        z-[100]

                        flex
                        items-center
                        justify-center

                        bg-black/70

                        p-3

                        backdrop-blur-sm

                        sm:p-5
                        lg:p-8
                    "
                    role="dialog"
                    aria-modal="true"
                    aria-label="가맹점주 모집 안내"
                    onMouseDown={(
                        event,
                    ) => {
                        if (
                            event.target ===
                            event.currentTarget
                        ) {
                            handleCloseNotice();
                        }
                    }}
                >

                    <div
                        className="
                            relative

                            flex

                            max-h-[94dvh]
                            max-w-[94vw]

                            items-center
                            justify-center

                            sm:max-h-[92dvh]
                            sm:max-w-[90vw]

                            lg:max-h-[90dvh]
                            lg:max-w-[85vw]
                        "
                    >

                        {/* 닫기 버튼 */}
                        <button
                            type="button"
                            onClick={
                                handleCloseNotice
                            }
                            className="
                                absolute
                                right-2
                                top-2
                                z-20

                                grid
                                h-9
                                w-9
                                place-items-center

                                rounded-full

                                bg-black/70
                                text-white

                                shadow-lg
                                backdrop-blur

                                transition

                                hover:bg-black/90
                                active:scale-95

                                sm:right-3
                                sm:top-3
                                sm:h-10
                                sm:w-10
                            "
                            aria-label="팝업 닫기"
                        >

                            <X
                                size={21}
                                strokeWidth={2.2}
                            />

                        </button>


                        {/* 마케팅 이미지 */}
                        <img
                            src="/marketing.png"
                            alt="가맹점주 모집"
                            className="
                                block

                                h-auto
                                w-auto

                                max-h-[94dvh]
                                max-w-[94vw]

                                rounded-2xl

                                object-contain

                                shadow-2xl

                                sm:max-h-[92dvh]
                                sm:max-w-[90vw]

                                lg:max-h-[90dvh]
                                lg:max-w-[85vw]
                            "
                        />

                    </div>

                </div>

            )}


            <div
                className="min-h-screen text-white"
                style={{
                    background:
                        "#0E0E0F",
                }}
            >

                {/* ── 네비바 ───────────────────────────── */}
                <nav
                    ref={navRef}
                    className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between px-8 py-4 transition-all duration-300"
                    style={{
                        background:
                            "transparent",
                    }}
                >

                    <div className="flex items-center gap-2">

                        <span className="landing-shimmer-text text-2xl font-bold tracking-widest">
                            La Poit
                        </span>

                    </div>


                    <div className="flex items-center gap-2">

                        <button
                            onClick={() =>
                                navigate(
                                    RouteConfig
                                        .Public
                                        .WithdrawalGuide
                                        .fullPath,
                                )
                            }
                            className="rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 hover:scale-105"
                            style={{
                                background:
                                    "rgba(213,156,57,0.18)",
                                color:
                                    "#F2D7A2",
                            }}
                            onMouseEnter={(
                                event,
                            ) => {
                                event
                                    .currentTarget
                                    .style
                                    .background =
                                    "rgba(213,156,57,0.28)";
                            }}
                            onMouseLeave={(
                                event,
                            ) => {
                                event
                                    .currentTarget
                                    .style
                                    .background =
                                    "rgba(213,156,57,0.18)";
                            }}
                        >
                            Q&amp;A
                        </button>


                        <button
                            onClick={() =>
                                navigate(
                                    RouteConfig
                                        .Auth
                                        .Login
                                        .fullPath,
                                )
                            }
                            className="rounded-full border px-4 py-1.5 text-sm font-medium transition-all duration-200 hover:scale-105"
                            style={{
                                borderColor:
                                    "#D59C39",
                                color:
                                    "#D59C39",
                            }}
                            onMouseEnter={(
                                event,
                            ) => {
                                event
                                    .currentTarget
                                    .style
                                    .background =
                                    "#D59C39";

                                event
                                    .currentTarget
                                    .style
                                    .color =
                                    "#0E0E0F";
                            }}
                            onMouseLeave={(
                                event,
                            ) => {
                                event
                                    .currentTarget
                                    .style
                                    .background =
                                    "transparent";

                                event
                                    .currentTarget
                                    .style
                                    .color =
                                    "#D59C39";
                            }}
                        >
                            로그인
                        </button>

                    </div>

                </nav>


                {/* ── 히어로 섹션 ─────────────────────── */}
                <section
                    className="relative flex items-center justify-center overflow-hidden"
                    style={{
                        minHeight:
                            "100vh",

                        background:
                            feltTexture,
                    }}
                >

                    <div
                        className="absolute inset-0 opacity-30"
                        style={{
                            backgroundImage: `repeating-linear-gradient(
                                0deg,
                                transparent,
                                transparent 3px,
                                rgba(255,255,255,0.012) 3px,
                                rgba(255,255,255,0.012) 4px
                            ),
                            repeating-linear-gradient(
                                90deg,
                                transparent,
                                transparent 3px,
                                rgba(255,255,255,0.012) 3px,
                                rgba(255,255,255,0.012) 4px
                            )`,
                        }}
                    />


                    <div
                        className="pointer-events-none absolute rounded-full opacity-20 blur-3xl"
                        style={{
                            width: 700,
                            height: 700,

                            background:
                                "radial-gradient(circle, #16a34a 0%, transparent 70%)",

                            top: "50%",
                            left: "50%",

                            transform:
                                "translate(-50%, -50%)",
                        }}
                    />


                    {CARDS.map(
                        (
                            card,
                            i,
                        ) => (

                            <div
                                key={i}
                                className="absolute cursor-default select-none transition-transform duration-200 hover:z-10 hover:scale-110"
                                style={{
                                    top:
                                        card.top,

                                    left:
                                        card.left,

                                    [
                                        "--card-rotation" as string
                                    ]:
                                        card.rot,

                                    animation:
                                        `dealCard 0.8s ${card.delay} cubic-bezier(.22,1,.36,1) both, floatCard ${card.dur} ${card.delay} ease-in-out infinite`,

                                    zIndex:
                                        2,
                                }}
                            >

                                <div
                                    className="flex flex-col items-center justify-center rounded-lg shadow-2xl"
                                    style={{
                                        width:
                                            52,

                                        height:
                                            72,

                                        background:
                                            "#fff",

                                        border:
                                            "2px solid #e5e7eb",

                                        color:
                                            isRedSuit(
                                                card.suit,
                                            )
                                                ? "#dc2626"
                                                : "#111",

                                        fontSize:
                                            11,

                                        fontWeight:
                                            700,

                                        lineHeight:
                                            1.2,

                                        padding:
                                            "4px 6px",

                                        boxShadow:
                                            "0 8px 24px rgba(0,0,0,0.5)",
                                    }}
                                >

                                    <span
                                        style={{
                                            alignSelf:
                                                "flex-start",

                                            fontSize:
                                                13,
                                        }}
                                    >
                                        {
                                            card.rank
                                        }
                                    </span>


                                    <span
                                        style={{
                                            fontSize:
                                                22,

                                            lineHeight:
                                                1,
                                        }}
                                    >
                                        {
                                            card.suit
                                        }
                                    </span>


                                    <span
                                        style={{
                                            alignSelf:
                                                "flex-end",

                                            fontSize:
                                                13,

                                            transform:
                                                "rotate(180deg)",
                                        }}
                                    >
                                        {
                                            card.rank
                                        }
                                    </span>

                                </div>

                            </div>

                        ),
                    )}


                    {CHIPS.map(
                        (
                            chip,
                            i,
                        ) => (

                            <div
                                key={i}
                                className="absolute cursor-default select-none"
                                style={{
                                    top:
                                        chip.top,

                                    left:
                                        chip.left,

                                    [
                                        "--card-rotation" as string
                                    ]:
                                        "0deg",

                                    animation:
                                        `dealCard 0.8s ${chip.delay} cubic-bezier(.22,1,.36,1) both, floatCard ${chip.dur} ${chip.delay} ease-in-out infinite`,

                                    zIndex:
                                        2,
                                }}
                            >

                                <PokerChip
                                    color={
                                        chip.color
                                    }
                                    inner={
                                        chip.inner
                                    }
                                />

                            </div>

                        ),
                    )}


                    <div
                        className="relative z-10 flex flex-col items-center px-6 text-center"
                        style={{
                            maxWidth:
                                720,
                        }}
                    >

                        <div className="mb-6 flex items-center gap-5">

                            {[
                                "♠",
                                "♥",
                                "♦",
                                "♣",
                            ].map(
                                (
                                    suit,
                                    i,
                                ) => (

                                    <span
                                        key={i}
                                        className="select-none text-4xl font-bold"
                                        style={{
                                            color:
                                                suit ===
                                                    "♥" ||
                                                suit ===
                                                    "♦"
                                                    ? "#f87171"
                                                    : "#E6C388",

                                            textShadow:
                                                "0 0 20px rgba(213,156,57,0.5)",

                                            animation:
                                                `dealCard 0.6s ${i * 0.12}s cubic-bezier(.22,1,.36,1) both`,
                                        }}
                                    >
                                        {suit}
                                    </span>

                                ),
                            )}

                        </div>


                        <h1
                            className="landing-shimmer-text mb-4 font-bold tracking-widest"
                            style={{
                                fontSize:
                                    "clamp(56px, 10vw, 110px)",

                                lineHeight:
                                    1.05,
                            }}
                        >
                            La Poit
                        </h1>


                        <p
                            className="mb-2 font-light uppercase tracking-widest"
                            style={{
                                fontSize:
                                    "clamp(13px, 2vw, 18px)",

                                color:
                                    "#D3AD66",

                                animation:
                                    "dealCard 0.8s 0.5s cubic-bezier(.22,1,.36,1) both",

                                letterSpacing:
                                    "0.25em",
                            }}
                        >
                            Holdem Pub
                        </p>


                        <div
                            className="mb-10"
                            style={{
                                width:
                                    120,

                                height:
                                    2,

                                background:
                                    "linear-gradient(90deg, transparent, #D59C39, transparent)",

                                animation:
                                    "dealCard 0.8s 0.8s cubic-bezier(.22,1,.36,1) both",
                            }}
                        />

                    </div>


                    <div
                        className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 opacity-60"
                        style={{
                            animation:
                                "bounceDown 1.8s ease-in-out infinite",
                        }}
                    >

                        <span
                            className="text-xs uppercase tracking-widest"
                            style={{
                                color:
                                    "#D59C39",
                            }}
                        >
                            Scroll
                        </span>


                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                        >
                            <path
                                d="M10 4v12M4 10l6 6 6-6"
                                stroke="#D59C39"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>

                    </div>

                </section>


                {/* ── About ─────────────────────────────── */}
                <section
                    className="px-6 py-28"
                    style={{
                        background:
                            "#0E0E0F",
                    }}
                >

                    <div className="mx-auto max-w-4xl text-center">

                        <FadeSection>

                            <p
                                className="mb-4 text-sm uppercase tracking-widest"
                                style={{
                                    color:
                                        "#D59C39",
                                }}
                            >
                                Welcome
                            </p>


                            <h2
                                className="mb-6 font-bold"
                                style={{
                                    fontSize:
                                        "clamp(28px, 5vw, 48px)",

                                    color:
                                        "#fff",
                                }}
                            >
                                La Poit에 오신 것을 환영합니다
                            </h2>


                            <div
                                className="mx-auto mb-8"
                                style={{
                                    width:
                                        64,

                                    height:
                                        2,

                                    background:
                                        "linear-gradient(90deg, transparent, #D59C39, transparent)",
                                }}
                            />


                            <p
                                className="font-light leading-relaxed"
                                style={{
                                    fontSize:
                                        18,

                                    color:
                                        "rgba(255,255,255,0.65)",

                                    maxWidth:
                                        600,

                                    margin:
                                        "0 auto",
                                }}
                            >
                                라 포이트는 최고의 홀덤 환경을 제공하는 홀덤펍입니다.
                                <br />
                                쾌적한 공간, 공정한 게임, 수준 높은 운영으로 여러분을 맞이합니다.
                            </p>

                        </FadeSection>

                    </div>

                </section>


                {/* ── 특징 ─────────────────────────────── */}
                <section
                    className="px-6 py-20"
                    style={{
                        background:
                            "#131314",
                    }}
                >

                    <div className="mx-auto max-w-5xl">

                        <FadeSection className="mb-16 text-center">

                            <p
                                className="mb-3 text-sm uppercase tracking-widest"
                                style={{
                                    color:
                                        "#D59C39",
                                }}
                            >
                                Features
                            </p>

                        </FadeSection>


                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">

                            {[
                                {
                                    icon:
                                        "♠",

                                    title:
                                        "프리미엄 게임 환경",

                                    desc:
                                        "쾌적하고 넓은 공간에서 최고의 홀덤 경험을 즐기세요.",

                                    delay:
                                        "0s",
                                },
                                {
                                    icon:
                                        "♦",

                                    title:
                                        "전문 딜러 운영",

                                    desc:
                                        "숙련된 전문 딜러가 공정하고 원활한 게임 진행을 보장합니다.",

                                    delay:
                                        "0.15s",
                                },
                                {
                                    icon:
                                        "♥",

                                    title:
                                        "다양한 토너먼트",

                                    desc:
                                        "정기 토너먼트와 특별 이벤트를 통해 더 큰 즐거움을 경험하세요.",

                                    delay:
                                        "0.3s",
                                },
                            ].map(
                                (
                                    feat,
                                    i,
                                ) => (

                                    <FadeSection
                                        key={i}
                                        delay={
                                            feat.delay
                                        }
                                    >

                                        <div
                                            className="h-full rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2"
                                            style={{
                                                background:
                                                    "#1a1a1b",

                                                border:
                                                    "1px solid rgba(213,156,57,0.2)",

                                                boxShadow:
                                                    "0 4px 32px rgba(0,0,0,0.4)",
                                            }}
                                            onMouseEnter={(
                                                event,
                                            ) => {
                                                event
                                                    .currentTarget
                                                    .style
                                                    .borderColor =
                                                    "rgba(213,156,57,0.6)";

                                                event
                                                    .currentTarget
                                                    .style
                                                    .boxShadow =
                                                    "0 8px 40px rgba(213,156,57,0.15)";
                                            }}
                                            onMouseLeave={(
                                                event,
                                            ) => {
                                                event
                                                    .currentTarget
                                                    .style
                                                    .borderColor =
                                                    "rgba(213,156,57,0.2)";

                                                event
                                                    .currentTarget
                                                    .style
                                                    .boxShadow =
                                                    "0 4px 32px rgba(0,0,0,0.4)";
                                            }}
                                        >

                                            <div
                                                className="mb-5 select-none text-4xl"
                                                style={{
                                                    color:
                                                        feat.icon ===
                                                        "♥"
                                                            ? "#f87171"
                                                            : "#D59C39",
                                                }}
                                            >
                                                {
                                                    feat.icon
                                                }
                                            </div>


                                            <h3
                                                className="mb-3 font-semibold"
                                                style={{
                                                    fontSize:
                                                        20,

                                                    color:
                                                        "#E6C388",
                                                }}
                                            >
                                                {
                                                    feat.title
                                                }
                                            </h3>


                                            <p
                                                style={{
                                                    fontSize:
                                                        15,

                                                    color:
                                                        "rgba(255,255,255,0.55)",

                                                    lineHeight:
                                                        1.7,
                                                }}
                                            >
                                                {
                                                    feat.desc
                                                }
                                            </p>

                                        </div>

                                    </FadeSection>

                                ),
                            )}

                        </div>

                    </div>

                </section>


                {/* ── 사업자 정보 ──────────────────────── */}
                <section
                    className="px-6 py-24"
                    style={{
                        background:
                            "#0E0E0F",
                    }}
                >

                    <div className="mx-auto max-w-3xl">

                        <FadeSection className="mb-12 text-center">

                            <p
                                className="mb-3 text-sm uppercase tracking-widest"
                                style={{
                                    color:
                                        "#D59C39",
                                }}
                            >
                                Location
                            </p>


                            <h2
                                className="font-bold"
                                style={{
                                    fontSize:
                                        "clamp(24px, 4vw, 40px)",

                                    color:
                                        "#fff",
                                }}
                            >
                                오시는 길
                            </h2>

                        </FadeSection>


                        <FadeSection delay="0.1s">

                            <div
                                className="rounded-2xl p-10"
                                style={{
                                    background:
                                        "linear-gradient(135deg, #1a1a1b 0%, #121213 100%)",

                                    border:
                                        "1px solid rgba(213,156,57,0.35)",

                                    boxShadow:
                                        "0 0 40px rgba(213,156,57,0.08)",
                                }}
                            >

                                <div
                                    className="mb-8 rounded-full"
                                    style={{
                                        height:
                                            3,

                                        background:
                                            "linear-gradient(90deg, #D59C39, #E6C388, #D59C39)",
                                    }}
                                />


                                <div className="space-y-5">

                                    {[
                                        {
                                            label:
                                                "상호",

                                            value:
                                                "라 포이트 홀덤펍 (La Poit)",
                                        },
                                        {
                                            label:
                                                "대표자",

                                            value:
                                                "김규석",
                                        },
                                        {
                                            label:
                                                "주소",

                                            value:
                                                "경상북도 구미시 인동중앙로11길 29, 주1동 2층 (황상동)",
                                        },
                                        {
                                            label:
                                                "우편번호",

                                            value:
                                                "39430",
                                        },
                                    ].map(
                                        (
                                            row,
                                            i,
                                        ) => (

                                            <div
                                                key={i}
                                                className="flex items-start gap-4"
                                            >

                                                <span
                                                    className="shrink-0 pt-0.5 text-sm font-medium"
                                                    style={{
                                                        color:
                                                            "#D59C39",

                                                        width:
                                                            72,

                                                        letterSpacing:
                                                            "0.05em",
                                                    }}
                                                >
                                                    {
                                                        row.label
                                                    }
                                                </span>


                                                <span
                                                    className="font-light"
                                                    style={{
                                                        color:
                                                            "rgba(255,255,255,0.8)",

                                                        fontSize:
                                                            15,

                                                        lineHeight:
                                                            1.6,
                                                    }}
                                                >
                                                    {
                                                        row.value
                                                    }
                                                </span>

                                            </div>

                                        ),
                                    )}

                                </div>


                                <div
                                    className="mt-8 rounded-full"
                                    style={{
                                        height:
                                            3,

                                        background:
                                            "linear-gradient(90deg, #D59C39, #E6C388, #D59C39)",
                                    }}
                                />

                            </div>

                        </FadeSection>

                    </div>

                </section>


                {/* ── Footer ───────────────────────────── */}
                <footer
                    className="px-6 py-10 text-center"
                    style={{
                        background:
                            "#090909",

                        borderTop:
                            "1px solid rgba(213,156,57,0.15)",
                    }}
                >

                    <div className="landing-shimmer-text mb-2 text-xl font-bold tracking-widest">
                        La Poit
                    </div>


                    <p
                        style={{
                            color:
                                "rgba(255,255,255,0.3)",

                            fontSize:
                                13,
                        }}
                    >
                        © {new Date().getFullYear()} La Poit Holdem Pub. All rights reserved.
                    </p>

                </footer>

            </div>

        </>
    );
};


export default LandingPage;